<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modalTitle"
    @on-confirm="saveContentTypeRights"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    maxWidth="80%"
  >
    <v-row v-if="rightsItems.length == 0" justify="center" align="center">
      <v-col cols="1">
        <v-btn
          tile
          color="primary"
          prepend-icon="mdi-plus"
          outlined
          @click="addContentTypeRights()"
        >
          {{ $t("Add") }}
        </v-btn>
      </v-col>
    </v-row>

    <div v-for="(item, index) in rightsItems" :key="item.uuid">
      <v-row align="center">
        <v-col cols="1" align-self="center">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                tile
                large
                color="error"
                icon
                @click="removeContentTypeRights(item.uuid)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Delete") }}</span>
          </v-tooltip>
        </v-col>
        <v-col align-self="center">
          <v-text-field
            readonly
            v-model="item.displayConditionStr"
            :label="$t('Condition')"
            :append-icon="item.conditionStr == '' ? 'mdi-plus' : 'mdi-pencil'"
            @click:append="editCondition(item)"
          ></v-text-field>
        </v-col>
        <v-col align-self="center">
          <v-select
            :label="$t('Role')"
            v-model="item.roleTypeID"
            :items="rolesList"
            item-text="text"
            item-value="id"
            :error-messages="roleErrors(index)"
          >
            <template slot="append-outer">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    color="success"
                    icon
                    @click="onRoleButtonClick"
                  >
                    <v-icon>mdi-table-plus</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("InsertData") }}</span>
              </v-tooltip>
            </template>
          </v-select>
        </v-col>
        <v-col align-self="center">
          <v-select
            :label="$t('AccessRight')"
            v-model="item.accessRightId"
            :items="accessRightsList"
            item-text="text"
            item-value="id"
            :error-messages="accessRightErrors(index)"
          >
            <template slot="append-outer">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    color="success"
                    icon
                    @click="onAccessRightButtonClick"
                  >
                    <v-icon>mdi-table-plus</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("InsertData") }}</span>
              </v-tooltip>
            </template>
          </v-select>
        </v-col>
        <v-col align-self="center">
          <v-select
            :label="$t('DigitalSignature')"
            v-model="item.digitalSignatureConfigId"
            :items="digitalSignaturesList"
            item-text="text"
            item-value="id"
            clearable
          >
            <template slot="append-outer">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    color="success"
                    icon
                    @click="onDigitalSignatureButtonClick"
                  >
                    <v-icon>mdi-table-plus</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("InsertData") }}</span>
              </v-tooltip>
            </template>
          </v-select>
        </v-col>
        <v-col cols="1" align-self="center">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                tile
                large
                color="primary"
                icon
                @click="addContentTypeRights()"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Add") }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </div>
    <template v-slot:actions>
      <div style="position: absolute">
        <v-menu bottom right>
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  text
                  small
                  v-bind="attrs"
                  v-on="{ ...tooltip, ...menu }"
                  color="blue-grey"
                >
                  <v-icon large>mdi-history</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("Versions") }}</span>
            </v-tooltip>
          </template>
          <v-list>
            <v-list-item
              v-for="(item, i) in versionList"
              :key="i"
              @click="onVersionChange(item)"
            >
              <v-list-item-title>{{ "v" + item.text }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </template>
    <SearchCriteriaBuilderModal
      :ref="conditionModalKey"
      :contentTypeIDs="[contentType.id]"
      @on-condition-saved="onConditionChanged"
      :allowEmptyCondition="true"
    />
    <RoleModal :ref="roleModalKey" @on-item-saved="getRoles" />
    <AccessRightModal
      :ref="accessRightModalKey"
      @on-item-saved="getAccessRights"
    />
    <DigitalSignatureModal
      :ref="digitalSignatureModalKey"
      @on-item-saved="getDigitalSignatures"
    />
  </base-modal>
</template>
<script>
import SearchCriteriaBuilderModal from "@/components/search/SearchCriteriaBuilderModal.vue";
import ContentType from "@/ecm/models/contentType/ContentType";
import ContentTypeField from "@/ecm/models/contentType/ContentTypeField";
import ContentTypeRight from "@/ecm/models/contentType/ContentTypeRight";
import ContentTypeService from "@/services/ContentTypeService";
import RoleService from "@/services/RoleService";
import RoleModal from "@/components/administration/role/RoleModal.vue";
import AccessRightModal from "@/components/administration/accessRight/AccessRightModal.vue";
import AccessRightService from "@/services/AccessRightService";
import DigitalSignatureService from "@/services/DigitalSignatureService";
import DigitalSignatureModal from "@/components/administration/digitalSignature/DigitalSignatureModal.vue";
import { required } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";

export default {
  name: "RightsModal",
  components: {
    SearchCriteriaBuilderModal,
    RoleModal,
    AccessRightModal,
    DigitalSignatureModal,
  },
  mounted() {
    this.getRoles();
    this.getAccessRights();
    this.getDigitalSignatures();
    this.$v.$reset();
  },
  data() {
    return {
      modal: {
        title: this.$t("ContentTypeAccessRights"),
        key: "contentTypeRightsModal",
      },
      contentType: new ContentType(),
      open: false,
      conditionModalOpen: false,
      rightsItems: [],
      currentItem: new ContentTypeRight(),
      versionList: [],
      rolesList: [],
      accessRightsList: [],
      digitalSignaturesList: [],
      conditionModalKey: "conditionModalKey",
      roleModalKey: "roleModalKey",
      accessRightModalKey: "accessRightModalKey",
      digitalSignatureModalKey: "digitalSignatureModalKey",
    };
  },
  mixins: [validationMixin],
  validations: {
    rightsItems: {
      $each: {
        roleTypeID: { required },
        accessRightId: { required },
      },
    },
  },
  computed: {
    modalTitle() {
      return `${this.modal.title} - ${this.contentType.name} ${this.contentType.version}`;
    },
    conditionModalInstance() {
      return this.$refs[this.conditionModalKey];
    },
    roleModalInstance() {
      return this.$refs[this.roleModalKey];
    },
    accessRightModalInstance() {
      return this.$refs[this.accessRightModalKey];
    },
    digitalSignatureModalInstance() {
      return this.$refs[this.digitalSignatureModalKey];
    },
  },
  methods: {
    roleErrors(index) {
      const errors = [];
      if (!this.$v.rightsItems.$each[index].roleTypeID.$dirty) return errors;
      !this.$v.rightsItems.$each[index].roleTypeID.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    onRoleButtonClick() {
      this.roleModalInstance.new();
    },
    onAccessRightButtonClick() {
      this.accessRightModalInstance.new();
    },
    onDigitalSignatureButtonClick() {
      this.digitalSignatureModalInstance.new();
    },
    accessRightErrors(index) {
      const errors = [];
      if (!this.$v.rightsItems.$each[index].accessRightId.$dirty) return errors;
      !this.$v.rightsItems.$each[index].accessRightId.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    edit(dto) {
      this.contentType = new ContentType(dto);
      this.getContentTypeRights();
      this.getVersions();
      this.getFields();
      this.show();
    },
    editCondition(item) {
      this.currentItem = item;
      this.conditionModalInstance.edit(item.conditionStr);
    },
    onConditionChanged(condition) {
      this.currentItem.conditionStr = condition.conditionStr;
      this.currentItem.displayConditionStr = condition.displayConditionStr;
    },
    onVersionChange(version) {
      if (this.contentType.id != version.id) {
        this.contentType.id = version.id;
        this.contentType.version = "v" + version.text;
        this.getContentTypeRights();
        this.getFields();
      }
    },
    getContentTypeRights() {
      ContentTypeService.getRights(this.contentType.id).then((data) => {
        this.rightsItems = data.map((dto) => new ContentTypeRight(dto));
      });
    },
    getVersions() {
      ContentTypeService.getVersions(this.contentType.id).then((data) => {
        this.versionList = data;
      });
    },
    getFields() {
      ContentTypeService.getFields([this.contentType.id]).then((data) => {
        this.contentType.fields = data.map(function (dto) {
          return new ContentTypeField(dto);
        });
      });
    },
    getRoles() {
      RoleService.getList().then((data) => {
        this.rolesList = data;
      });
    },
    getAccessRights() {
      AccessRightService.getList().then((data) => {
        this.accessRightsList = data;
      });
    },
    getDigitalSignatures() {
      DigitalSignatureService.getList().then((data) => {
        this.digitalSignaturesList = data;
      });
    },
    saveContentTypeRights() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }
      var rights = this.rightsItems.map((item) => item.toDto());
      ContentTypeService.saveRights(this.contentType.id, rights).then(
        (data) => {
          this.$emit("on-rights-saved", this.rightsItems);
          this.$store.dispatch("account/getUserContentTypes");
          this.$store.dispatch("account/getUserMenu");
          this.hide();
        }
      );
    },
    addContentTypeRights() {
      this.rightsItems.push(new ContentTypeRight());
    },
    removeContentTypeRights(uuid) {
      this.rightsItems = this.rightsItems.filter((item) => item.uuid !== uuid);
    },
    show() {
      this.open = true;
    },
    hide() {
      this.open = false;
    },
  },
};
</script>
